// NobleBlade - Slide-out panels for forms and details
// Replaces modals with smooth slide-in panels from right
// Supports stacking multiple blades (primary, secondary)
// Features: Backdrop blur, keyboard shortcuts (ESC), scroll lock

import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import styled from '@emotion/styled';
import { motion, AnimatePresence } from 'framer-motion';
import { CloseOutlined } from '@ant-design/icons';
import { NobleColors, NobleSpacing, NobleShadows, NobleTypography } from '../../../theme/noble-tokens';
import { NobleButton } from '../NobleButton';

export interface NobleBladeProps {
  open: boolean;
  onClose: () => void;
  title?: React.ReactNode;
  children?: React.ReactNode;
  footer?: React.ReactNode;
  width?: 'small' | 'medium' | 'large' | 'full' | number;
  level?: 'primary' | 'secondary'; // For stacking blades
  showBackdrop?: boolean;
  closeOnBackdropClick?: boolean;
  closeOnEscape?: boolean;
  className?: string;
}

// Width mapping
const BLADE_WIDTHS = {
  small: '400px',
  medium: '600px',
  large: '800px',
  full: '100vw',
};

// Styled Components
const BladeBackdrop = styled(motion.div)<{ $level: 'primary' | 'secondary' }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: ${({ $level }) =>
    $level === 'primary' ? 'rgba(0, 0, 0, 0.5)' : 'rgba(0, 0, 0, 0.2)'};
  backdrop-filter: blur(4px);
  z-index: ${({ $level }) => ($level === 'primary' ? 1100 : 1200)};
  display: flex;
  align-items: center;
  justify-content: flex-end;
`;

const BladeContainer = styled(motion.div)<{ $width: string; $level: 'primary' | 'secondary' }>`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  width: ${({ $width }) => $width};
  max-width: 100vw;
  background: ${NobleColors['level-0-light']};
  box-shadow: ${NobleShadows['elevation-4']};
  display: flex;
  flex-direction: column;
  z-index: ${({ $level }) => ($level === 'primary' ? 1101 : 1201)};
  overflow: hidden;

  /* Add left border for visual depth */
  border-left: 2px solid ${NobleColors['steel-gray-base']};

  /* Mobile responsive */
  @media (max-width: 768px) {
    width: 100vw;
    max-width: 100vw;
  }
`;

const BladeHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${NobleSpacing.lg} ${NobleSpacing.xl};
  background: linear-gradient(180deg, ${NobleColors['level-1-light']} 0%, ${NobleColors['level-0-light']} 100%);
  border-bottom: 2px solid ${NobleColors['steel-gray-base']};
  min-height: 72px;
  flex-shrink: 0;
`;

const BladeTitle = styled.div`
  font-size: ${NobleTypography['h3-desktop-size']};
  font-weight: ${NobleTypography['h3-weight']};
  color: ${NobleColors['steel-gray-dark']};
  font-family: ${NobleTypography['font-primary']};
  line-height: ${NobleTypography['h3-line-height']};
  flex: 1;
  margin-right: ${NobleSpacing.md};
`;

const BladeCloseButton = styled(motion.button)`
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background: transparent;
  border: 2px solid ${NobleColors['steel-gray-base']};
  color: ${NobleColors['steel-gray-dark']};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 18px;
  transition: all 0.2s ease-out;
  flex-shrink: 0;

  &:hover {
    background: ${NobleColors['critical-red-base']};
    border-color: ${NobleColors['critical-red-base']};
    color: white;
    transform: rotate(90deg);
  }

  &:active {
    transform: rotate(90deg) scale(0.95);
  }
`;

const BladeContent = styled.div`
  flex: 1;
  padding: ${NobleSpacing.xl};
  overflow-y: auto;
  overflow-x: hidden;

  /* Custom scrollbar */
  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: ${NobleColors['level-0-light']};
  }

  &::-webkit-scrollbar-thumb {
    background: ${NobleColors['steel-gray-base']};
    border-radius: 4px;

    &:hover {
      background: ${NobleColors['steel-gray-dark']};
    }
  }

  /* Mobile responsive */
  @media (max-width: 768px) {
    padding: ${NobleSpacing.lg};
  }
`;

const BladeFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: ${NobleSpacing.md};
  padding: ${NobleSpacing.lg} ${NobleSpacing.xl};
  background: linear-gradient(180deg, ${NobleColors['level-0-light']} 0%, ${NobleColors['level-1-light']} 100%);
  border-top: 2px solid ${NobleColors['steel-gray-base']};
  min-height: 80px;
  flex-shrink: 0;

  /* Mobile responsive - stack buttons */
  @media (max-width: 768px) {
    flex-direction: column;
    gap: ${NobleSpacing.sm};
    padding: ${NobleSpacing.md} ${NobleSpacing.lg};

    button {
      width: 100%;
    }
  }
`;

// Main Component
export const NobleBlade: React.FC<NobleBladeProps> = ({
  open,
  onClose,
  title,
  children,
  footer,
  width = 'medium',
  level = 'primary',
  showBackdrop = true,
  closeOnBackdropClick = true,
  closeOnEscape = true,
  className,
}) => {
  const bladeRef = useRef<HTMLDivElement>(null);

  // Calculate width
  const bladeWidth = typeof width === 'number' ? `${width}px` : BLADE_WIDTHS[width];

  // Handle ESC key
  useEffect(() => {
    if (!open || !closeOnEscape) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [open, closeOnEscape, onClose]);

  // Prevent body scroll when blade is open
  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  // Focus trap (focus blade when opened)
  useEffect(() => {
    if (open && bladeRef.current) {
      bladeRef.current.focus();
    }
  }, [open]);

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (closeOnBackdropClick && e.target === e.currentTarget) {
      onClose();
    }
  };

  const bladeContent = (
    <AnimatePresence>
      {open && (
        <>
          {showBackdrop && (
            <BladeBackdrop
              $level={level}
              onClick={handleBackdropClick}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
            />
          )}
          <BladeContainer
            ref={bladeRef}
            $width={bladeWidth}
            $level={level}
            className={className}
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            tabIndex={-1}
            role="dialog"
            aria-modal="true"
          >
            {/* Header */}
            <BladeHeader>
              <BladeTitle>{title}</BladeTitle>
              <BladeCloseButton
                onClick={onClose}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                aria-label="Close"
              >
                <CloseOutlined />
              </BladeCloseButton>
            </BladeHeader>

            {/* Content */}
            <BladeContent>{children}</BladeContent>

            {/* Footer */}
            {footer && <BladeFooter>{footer}</BladeFooter>}
          </BladeContainer>
        </>
      )}
    </AnimatePresence>
  );

  // Render in portal to body
  return createPortal(bladeContent, document.body);
};

// Helper hook for blade state management
export const useNobleBlade = (initialOpen = false) => {
  const [isOpen, setIsOpen] = React.useState(initialOpen);

  const openBlade = () => setIsOpen(true);
  const closeBlade = () => setIsOpen(false);
  const toggleBlade = () => setIsOpen((prev) => !prev);

  return {
    isOpen,
    openBlade,
    closeBlade,
    toggleBlade,
  };
};

// Pre-configured Blades
export const SmallBlade: React.FC<Omit<NobleBladeProps, 'width'>> = (props) => (
  <NobleBlade {...props} width="small" />
);

export const MediumBlade: React.FC<Omit<NobleBladeProps, 'width'>> = (props) => (
  <NobleBlade {...props} width="medium" />
);

export const LargeBlade: React.FC<Omit<NobleBladeProps, 'width'>> = (props) => (
  <NobleBlade {...props} width="large" />
);

export const FullBlade: React.FC<Omit<NobleBladeProps, 'width'>> = (props) => (
  <NobleBlade {...props} width="full" />
);

